import React from 'react';
import { Box, Button, Flex, Menu, MenuItem, MenuSeparator, MenuTrigger, TagBadge, Text } from '@chatsdet/ui';
import { DotsThreeVertical } from '@phosphor-icons/react';
import { CopyValueButton } from '../CopyValueButton';
import { useUpdateClientMutation, useRotateClientSecretMutation } from './useTestSignIn';
import type { TestSignInClient } from './types';

type Props = {
  client: TestSignInClient;
  propertyLabel: string;
  isAdmin: boolean;
  onEdit: () => void;
  onSetupInstructions: () => void;
  onSecretRotated: (secret: string) => void;
};

export function TestSignInClientRow({
  client,
  propertyLabel,
  isAdmin,
  onEdit,
  onSetupInstructions,
  onSecretRotated,
}: Props) {
  const updateClient = useUpdateClientMutation();
  const rotateSecret = useRotateClientSecretMutation();

  return (
    <Box className="p-4 border border-border rounded-lg">
      <Flex align="start" justify="between" gap="md">
        <Box className="min-w-0 flex-1">
          <Flex align="center" gap="sm" className="mb-1">
            <Text weight="medium">{propertyLabel}</Text>
            {client.disabled && <TagBadge name="Disabled" color="gray" size="sm" />}
          </Flex>
          <Flex align="center" gap="xs" className="mb-1">
            <Text size="xs" className="text-muted-foreground font-mono">
              {client.id}
            </Text>
            <CopyValueButton value={client.id} label="client ID" />
          </Flex>
          <Box className="space-y-0.5 mt-1.5">
            {client.redirectUris.map((uri) => (
              <Text key={uri} size="xs" className="text-muted-foreground font-mono break-all">
                {uri}
              </Text>
            ))}
          </Box>
          <Text size="xs" className="text-muted-foreground mt-1.5">
            Secret: {client.clientSecretPrefix}...
          </Text>
        </Box>
        <Flex align="center" gap="sm" className="shrink-0">
          <Button variant="outline" size="sm" onPress={onSetupInstructions}>
            Setup instructions
          </Button>
          {isAdmin && (
            <MenuTrigger>
              <Button variant="ghost" size="sm" aria-label={`Actions for ${propertyLabel}`}>
                <DotsThreeVertical size={16} weight="bold" />
              </Button>
              <Menu
                onAction={(key) => {
                  if (key === 'edit') {
                    onEdit();
                  } else if (key === 'toggle') {
                    updateClient.mutate({ clientId: client.id, disabled: !client.disabled });
                  } else if (key === 'rotate') {
                    rotateSecret.mutate(client.id, {
                      onSuccess: (result) => onSecretRotated(result.data.clientSecret),
                    });
                  }
                }}
              >
                <MenuItem id="edit">Edit callback URLs</MenuItem>
                <MenuItem id="rotate">Rotate secret</MenuItem>
                <MenuSeparator />
                <MenuItem id="toggle">{client.disabled ? 'Enable' : 'Disable'}</MenuItem>
              </Menu>
            </MenuTrigger>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}
